// src/components/Tools.tsx
import React from "react";
import { Box, Avatar, Popover, Button, MenuList, MenuItem, ListItemIcon } from "@mui/material";
import { CaretDown, CaretUp, Check } from "@phosphor-icons/react";
import ClientSearch from "components/ClientSearch";
import AvatarInitials from "./AvatarInitials";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/styles";

// —— NotificationCenter imports ——
import { useGlobalToast } from "components/GlobalToastProvider";
import { useNotifications } from "hooks/useNotifications";
import NotificationBell from "components/NotificationCenter/NotificationBell";
import NotificationDropdown from "components/NotificationCenter/NotificationDropdown";
import NotificationConfirmDeactivationDialog from "components/NotificationCenter/NotificationConfirmDeactivationDialog";
import { useSelector } from "react-redux";
import * as selectors from "selectors";
// ——————————————————————————

export const Tools = ({
  handleSearchChange,
  menuItems,
  userAvatarSrc,
  userFullName,
  userInitials,
  userPermissions,
}) => {
  const userId = useSelector(selectors.getUserId);
  const theme = useTheme();
  const { showToast, handleClose: hideToast } = useGlobalToast();

  // hook powers the bell/dropdown/snackbar
  const Notifications = useNotifications(userId);

  const anchorElMenu = React.useRef(null);
  const [openMenu, setOpenMenu] = React.useState(false);
  const [openSubMenu, setOpenSubMenu] = React.useState(false);
  const [anchorElSubMenu, setAnchorElSubMenu] = React.useState(null);
  const [subMenuItems, setSubMenuItems] = React.useState([]);

  const toggleMenu = () => setOpenMenu((o) => !o);
  const toggleSubMenu = () => setOpenSubMenu((o) => !o);

  const onMenuItemClick = (clickHandler) => {
    setOpenMenu(false);
    clickHandler();
  };

  const onEventMenuItemClick = (e, items, clickHandler) => {
    setAnchorElSubMenu(e.currentTarget);
    setSubMenuItems(items);
    setOpenSubMenu(true);
    clickHandler();
  };

  const {
    notifications,
    unreadCount,
    anchorEl,
    hasMore,
    loadingMore,
    deactivationDialogOpen,
    deactivationDialogData,
    unreadAssessmentNotifications,
    handlers: {
      handleOpen: handleBellOpen,
      handleClose: handleBellClose,
      handleMarkAll,
      handleClick: handleNotificationClick,
      handleLoadMore,
      handleCloseDeactivationDialog,
      handleToastView,
      handleToastDismiss,
    },
  } = Notifications;

  // Toasts for every unread assessment, keyed by notification id and holding the id the provider
  // gave each toast — so each one can be retracted on its own without touching the others.
  const assessmentToastIds = React.useRef(new Map());

  // What the effect follows: the set of unread assessments, not the objects, which the 5s poll
  // replaces on every run.
  const unreadAssessmentIds = (unreadAssessmentNotifications ?? []).map((n) => n.id).join(",");

  // Raised through the one provider the app already mounts, rather than a second GlobalToast:
  // two Snackbars share the same anchor and the later one hides the other completely.
  React.useEffect(() => {
    const unread = unreadAssessmentNotifications ?? [];
    const unreadIds = new Set(unread.map((n) => n.id));
    const raised = assessmentToastIds.current;

    // The toasts are a projection of server state: once a row is read — from the bell, from
    // another tab — its toast has to go, or a persistent toast sits there showing stale copy.
    raised.forEach((toastId, notificationId) => {
      if (unreadIds.has(notificationId)) return;
      hideToast(toastId);
      raised.delete(notificationId);
    });

    // The list comes newest first and the stack puts the last toast raised on top, so raise
    // oldest first to keep the newest assessment on top.
    [...unread].reverse().forEach((notification) => {
      if (raised.has(notification.id)) return;

      const retract = () => {
        hideToast(raised.get(notification.id));
        raised.delete(notification.id);
      };

      raised.set(
        notification.id,
        showToast({
          // One key per assessment: if this runs again for the same row — a remount of the
          // header, say — the provider hands back the toast already up instead of a copy.
          key: `assessment-${notification.id}`,
          message: `New Assessment Call Scheduled with ${
            notification.metadata?.client_name || "your client"
          }`,
          persistent: true,
          actions: [
            {
              label: "View Profile",
              onClick: () => {
                retract();
                handleToastView(notification);
              },
            },
            {
              label: "Dismiss",
              onClick: () => {
                retract();
                handleToastDismiss(notification);
              },
            },
          ],
        })
      );
    });
  }, [unreadAssessmentIds]);

  return (
    <Box display="flex" flexDirection="row" width="100%" justifyContent="space-between" py={2}>
      {userPermissions?.search_clients ? (
        <ClientSearch onSelectSearchOption={handleSearchChange} />
      ) : (
        <div />
      )}

      <Box minWidth="50%" display="flex" alignItems="center" justifyContent="flex-end">
        {userId && userPermissions?.view_notifications && (
          <>
            <NotificationBell unreadCount={unreadCount} onClick={handleBellOpen} />

            <NotificationDropdown
              anchorEl={anchorEl}
              notifications={notifications}
              hasMore={hasMore}
              loadingMore={loadingMore}
              onClose={handleBellClose}
              onMarkAll={handleMarkAll}
              onLoadMore={handleLoadMore}
              onItemClick={handleNotificationClick}
            />
          </>
        )}
        <Button
          ref={anchorElMenu}
          startIcon={
            userAvatarSrc || !userInitials ? (
              <Avatar src={userAvatarSrc} sx={{ width: 24, height: 24 }} />
            ) : (
              <AvatarInitials userInitials={userInitials} />
            )
          }
          endIcon={openMenu ? <CaretUp size={20} /> : <CaretDown size={20} />}
          variant="text"
          onClick={toggleMenu}
          color="inherit"
        >
          {userFullName}
        </Button>

        <Popover
          anchorEl={anchorElMenu.current}
          open={openMenu}
          onClose={toggleMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{ style: { minWidth: 150 } }}
        >
          <MenuList>
            {menuItems.map((item, i) => (
              <MenuItem
                key={i}
                disabled={item.disabled}
                divider={item.divider}
                onClick={(e) =>
                  !item.subMenuItems
                    ? onMenuItemClick(item.clickHandler)
                    : onEventMenuItemClick(e, item.subMenuItems, item.clickHandler)
                }
              >
                <Box display="flex" justifyContent="space-between" width="100%">
                  <Box display="flex" alignItems="center">
                    {item.icon && (
                      <ListItemIcon>
                        {React.createElement(item.icon, {
                          weight: "duotone",
                          color: "text.secondary",
                        })}
                      </ListItemIcon>
                    )}
                    {item.typography ? (
                      <Typography variant="caption" color="text.secondary">
                        {item.label}
                      </Typography>
                    ) : (
                      item.label
                    )}
                  </Box>
                  {item.endIcon && (
                    <Box display="flex" alignItems="center">
                      {React.createElement(item.endIcon, {
                        color: "text.secondary",
                      })}
                    </Box>
                  )}
                </Box>
              </MenuItem>
            ))}
          </MenuList>
        </Popover>

        <Popover
          anchorEl={anchorElSubMenu}
          open={openSubMenu}
          onClose={toggleSubMenu}
          anchorOrigin={{ vertical: "center", horizontal: "left" }}
          transformOrigin={{ vertical: "center", horizontal: "right" }}
          // PaperProps={{ style: { minWidth: 264, padding: theme.spacing(1) } }}
        >
          <MenuList>
            {subMenuItems.map((itm, idx) => (
              <MenuItem key={idx} onClick={itm.clickHandler}>
                {itm.avatar && (
                  <Avatar src={itm.avatarUrl} sx={{ mr: theme.spacing(1) }}>
                    {itm.avatarInitials}
                  </Avatar>
                )}
                <Typography flexGrow={1} variant="body2">
                  {itm.label}
                </Typography>
                {itm.checked && <Check size={24} color={theme.palette.primary.main} />}
              </MenuItem>
            ))}
          </MenuList>
        </Popover>

        {/* Deactivation Confirmation Dialog */}
        {deactivationDialogData && (
          <NotificationConfirmDeactivationDialog
            open={deactivationDialogOpen}
            onClose={handleCloseDeactivationDialog}
            clientId={deactivationDialogData.clientId}
            clientName={deactivationDialogData.clientName}
          />
        )}
      </Box>
    </Box>
  );
};
